import * as React from "react";
import { ThemeProvider as NextThemesProvider } from "next-themes";
import { type ThemeProviderProps } from "next-themes";
import { UIContext } from "../../providers/UIProvider";

interface Theme {
  dark: {};
  light: {};
  globals: {};
}

const defaultThemeColors: Theme = {
  dark: {},
  light: {},
  globals: {},
};

interface CustomThemeProviderProps extends ThemeProviderProps {
  themeColors?: Theme;
}

export function ThemeProvider({
  children,
  themeColors = defaultThemeColors,
  ...props
}: CustomThemeProviderProps) {
  const { theme } = React.useContext(UIContext);
  React.useEffect(() => {
    const applyCSSVars = (vars: Record<string, string>) => {
      const root = document.documentElement;
      Object.entries(vars).forEach(([key, value]) => {
        root.style.setProperty(`--${key}`, value);
      });
    };

    const applyTheme = (currentTheme: "dark" | "light") => {
      const themeColor =
        currentTheme === "dark" ? themeColors.dark : themeColors.light;
      Object.entries(themeColor).forEach(([key, value]) => {
        document.documentElement.style.setProperty(`--${key}`, value as string);
      });
    };

    // 🔁 Merge user-passed globals with defaults
    const mergedGlobalVars = {
      ...(themeColors.globals || {}),
    };

    applyCSSVars(mergedGlobalVars);
    // Initial theme application
    const storedTheme = theme;
    applyTheme(storedTheme || "light");

    // Listen for theme changes
    const handleThemeChange = (
      event: CustomEvent<{ theme: "dark" | "light" }>
    ) => {
      applyTheme(event.detail.theme);
    };

    window.addEventListener("themeChange", handleThemeChange as EventListener);

    return () => {
      window.removeEventListener(
        "themeChange",
        handleThemeChange as EventListener
      );
    };
  }, [themeColors, theme]);

  return (
    <NextThemesProvider
      attribute="data-theme"
      value={{
        light: "light",
        dark: "dark",
      }}
      defaultTheme="light"
      enableSystem
      {...props}
    >
      {children}
    </NextThemesProvider>
  );
}
